import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import { ConvexReactClient } from 'convex/react';
import { ConvexProviderWithAuth0 } from 'convex/react-auth0';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import App from './App';
import { getAuth0Config } from './config/auth0';
import { getConvexUrl } from './config/convex';
import './styles/index.css';

const mantineTheme = createTheme({
  primaryColor: 'blue',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  defaultRadius: 'md',
  colors: {
    customs: [
      '#e8f4f8', '#d1e9f1', '#a3d3e3', '#75bdd5', '#47a7c7',
      '#1e8fb9', '#186f90', '#125068', '#0c3040', '#061818',
    ],
  },
});

type ErrorBoundaryState = {
  hasError: boolean;
  message: string;
};

class GlobalErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      message: error?.message || 'Nepoznata runtime greška',
    };
  }

  componentDidCatch(error: Error) {
    console.error('Frontend runtime error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', padding: 24, background: '#efe5d4', color: '#1e3a5f' }}>
          <h1 style={{ marginBottom: 12 }}>Aplikacija je naišla na grešku</h1>
          <p style={{ marginBottom: 8 }}>Frontend je učitan, ali je došlo do runtime greške.</p>
          <p style={{ fontWeight: 600 }}>Detalj: {this.state.message}</p>
          <p style={{ marginTop: 12 }}>Otvorite Browser Console za puni stack trace.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

window.addEventListener('error', (event) => {
  console.error('Global window error:', event.error || event.message);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});

// Clean up Auth0 callback URL parameters
function cleanupAuthUrl() {
  const url = new URL(window.location.href);
  const hasAuthParams = url.searchParams.has('code') || url.searchParams.has('state') || url.searchParams.has('error');
  
  if (hasAuthParams) {
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    window.history.replaceState({}, document.title, url.pathname);
  }
}

function AuthConfigError({ message }: { message: string }): React.ReactElement {
  return (
    <div style={{ minHeight: '100vh', padding: 24, background: '#efe5d4', color: '#1e3a5f' }}>
      <h1 style={{ marginBottom: 12, color: '#dc2626' }}>⚠️ Auth0 konfiguracija nije validna</h1>
      <p style={{ marginBottom: 8 }}>Aplikacija radi u fail-closed režimu dok autentifikacija nije pravilno podešena.</p>
      <p style={{ fontWeight: 600 }}>Detalj: {message}</p>
    </div>
  );
}

// Auth0 wrapper (fail-closed)
function AuthWrapper({ children }: { children: React.ReactNode }) {
  let config: ReturnType<typeof getAuth0Config>;
  let convexUrl: string;

  try {
    config = getAuth0Config();
    convexUrl = getConvexUrl();
  } catch (error) {
    const message = error instanceof Error
      ? error.message
      : 'Auth0 configuration is missing or invalid.';
    return <AuthConfigError message={message} />;
  }

  const convex = new ConvexReactClient(convexUrl);
  
  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: 'openid profile email',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      onRedirectCallback={() => {
        // Clean URL after successful login
        cleanupAuthUrl();
      }}
    >
      <ConvexProviderWithAuth0 client={convex}>
        {children}
      </ConvexProviderWithAuth0>
    </Auth0Provider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AuthWrapper>
      <MantineProvider theme={mantineTheme}>
        <Notifications position="top-right" zIndex={2077} />
        <ModalsProvider>
          <GlobalErrorBoundary>
            <App />
          </GlobalErrorBoundary>
        </ModalsProvider>
      </MantineProvider>
    </AuthWrapper>
  </React.StrictMode>
);
